import React, { useEffect, useState } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

interface ToastProps {
  message: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({
  message,
  type = 'info',
  duration = 5000,
  onClose
}) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300); // Wait for fade out animation
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          backgroundColor: '#d4edda',
          borderColor: '#c3e6cb',
          color: '#155724',
          icon: <CheckCircle size={18} style={{ display: 'inline', verticalAlign: 'middle' }} />
        };
      case 'error':
        return {
          backgroundColor: '#f8d7da',
          borderColor: '#f5c6cb',
          color: '#721c24',
          icon: <XCircle size={18} style={{ display: 'inline', verticalAlign: 'middle' }} />
        };
      case 'warning':
        return {
          backgroundColor: '#fff3cd',
          borderColor: '#ffeaa7',
          color: '#856404',
          icon: <AlertTriangle size={18} style={{ display: 'inline', verticalAlign: 'middle' }} />
        };
      default:
        return {
          backgroundColor: '#d1ecf1',
          borderColor: '#bee5eb',
          color: '#0c5460',
          icon: <Info size={18} style={{ display: 'inline', verticalAlign: 'middle' }} />
        };
    }
  };

  const typeStyles = getTypeStyles();

  return (
    <div
      style={{
        ...toastStyle,
        ...typeStyles,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(-20px)',
      }}
    >
      <div style={contentStyle}>
        <span style={iconStyle}>{typeStyles.icon}</span>
        <span style={messageStyle}>{message}</span>
      </div>
      <button
        onClick={() => {
          setIsVisible(false);
          setTimeout(onClose, 300);
        }}
        style={closeButtonStyle}
      >
        <X size={16} />
      </button>
    </div>
  );
};

// Toast Container Component
interface ToastContainerProps {
  toasts: Array<{
    id: string;
    message: string;
    type?: 'success' | 'error' | 'warning' | 'info';
    duration?: number;
  }>;
  onRemoveToast: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({
  toasts,
  onRemoveToast
}) => {
  return (
    <div style={containerStyle}>
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => onRemoveToast(toast.id)}
        />
      ))}
    </div>
  );
};

// Styles
const toastStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '1rem',
  marginBottom: '0.5rem',
  border: '1px solid',
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  minWidth: '300px',
  maxWidth: '500px',
};

const containerStyle: React.CSSProperties = {
  position: 'fixed',
  top: '20px',
  right: '20px',
  zIndex: 9999,
  display: 'flex',
  flexDirection: 'column',
};

const contentStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flex: 1,
};

const iconStyle: React.CSSProperties = {
  marginRight: '0.75rem',
  fontSize: '1.2rem',
};

const messageStyle: React.CSSProperties = {
  fontSize: '0.9rem',
  fontWeight: '500',
  lineHeight: '1.4',
};

const closeButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  fontSize: '1.2rem',
  cursor: 'pointer',
  marginLeft: '1rem',
  opacity: 0.7,
  transition: 'opacity 0.2s',
};

export default Toast;
